import {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {Issue} from "../../../domain/entities/issue.entity";
import {Employee} from "../../../domain/entities/employee.entity";
import {property} from "lit/decorators/property.js";
import {Project} from "../../../domain/entities/project.entity";
import {
    bindTo,
    ComplexFieldConfiguration,
    ComplexFormControl,
    FieldType,
    FormBuilder,
    FormControlNotification,
    IFormControl,
    MinLengthObjectValidator,
    MinLengthStringValidator,
    PatternStringValidator,
    PrimitiveFieldConfiguration,
    PrimitiveFormControl,
    RequiredValidator,
    UrlStringValidator
} from "@ha/formhandler";
import {state} from "lit/decorators/state.js";
import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {get, translate} from "lit-translate";
import {customElement} from "lit/decorators/custom-element.js";
import {DateTime, Interval} from "luxon";
import {container} from "tsyringe";
import {IssueRepository} from "../../../domain/repositories/issue.repository";
import {DateHelper} from "../../../helpers/date.helper";
import {repeat} from "lit/directives/repeat.js";
import {debounceTime, exhaustMap, filter, merge, Subject, takeUntil} from "rxjs";
import {MilestoneStartBeforeEndValidator} from "../../../validators/milestone-start-before-end.validator";
import {ICommand} from "../../../commands/i-command.interface";
import {CreateProjectCommand} from "../../../commands/create-project.command";
import {CreateIssueCommand} from "../../../commands/create-issue.command";
import {CommandBus} from "../../../services/command-bus.service";
import {BulkCommand} from "../../../commands/bulk.command";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {when} from "lit/directives/when.js";
import {UpdateIssueCommand} from "../../../commands/update-issue.command";
import {DeleteIssueCommand} from "../../../commands/delete-issue.command";
import {deletedMilestones} from "./deleted-milestones";
import {UpdateProjectCommand} from "../../../commands/update-project.command";
import {BaseMessage} from "../../../message-broker/base-message.interface";
import {MessageBroker} from "../../../message-broker/message-broker";
import {ProjectRepository} from "../../../domain/repositories/project.repository";
import {coerceUrlToNullIfBlank} from "../../../helpers/url-coercion.helper";
import {renderTextColorFieldOfForm} from "../../_inputs/ha-input-color/text-color-field.template";

/**
 * Value of one milestone's complex form control. `end` exists only while `isMultipleDays`
 * is true: the `isMultipleDays` listener in `_initializeForm` adds and removes the field.
 */
type MilestoneFormValue = {
    start: DateTime;
    end?: DateTime;
    isMultipleDays: boolean;
}

/** Payload of one milestone's complex form control (set in `_initializeForm` and `_onClickAddMilestone`). */
type MilestonePayload = {
    milestone: Issue;
}

type FormData = {
    title: string;
    creator: Employee;
    assignees: Employee[];
    url: string;
    description: string;
    shortcut: string;
    color: string;
    /** null = "Automatisch" (feature plan 0074) */
    textColor: string | null;
    // ComplexFormControl.getValue() builds an object keyed by child key, here the milestone uid.
    milestones: Record<string, MilestoneFormValue>;
}

@customElement(`ha-dialog-project-edit`)
export class HaDialogProjectEditComponent extends AbstractDialogComponent {

    @property({type: Object})
    public project: Project | null = null;
    @property({type: String})
    public title = `Project Edit`;
    private _initialTitle: string | null = null;
    /**
     * Ends the streams of the current form; the form is rebuilt on every reset and reload. Kept
     * apart from `_disconnected$`, which the entity listener is bound to: ending that on a reset
     * stopped the listener, so the next save left the form dirty.
     */
    private readonly _formTeardown$ = new Subject<void>();
    /** True while the dialog's own save runs; its broker messages must not start a second reload. */
    private _saving = false;
    /** Uids of the milestones the current form was built from, for matching broker messages synchronously. */
    private _milestoneUids = new Set<string>();
    @state()
    private _form: ComplexFormControl<FormData, void> | null = null;
    private _changedValues: Map<keyof FormData, FormControlNotification> = new Map<keyof FormData, FormControlNotification>();

    private get _isSavable(): boolean {
        return this.project!.isNew()
            ? this._form?.isValid ?? false
            : (this._form?.isValid && this._form.isDirty) ?? false;
    };

    public connectedCallback() {
        super.connectedCallback();
        this._registerListeners();
    }

    /** @internal Public so unit tests can run the lifecycle step Lit calls. */
    public willUpdate(changedProperties: PropertyValues): void {
        if (changedProperties.has(`project`) && this.project instanceof Project) {
            this._initializeForm().then();
        }
    }

    protected onClickClose(): void {
        DialogHelper.closeDialog(this.tagName);
        super.onClickClose();
    }

    protected _renderMainContent(): unknown {
        if (!this._form) {
            return html`
                <div>Loading...</div>`
        }

        return html`
            <ha-form-project>
                <header class="formular__header">
                    ${this._renderActionButtons()}
                </header>
                <main class="formular__main">
                    <div class="input-wrapper__title">
                        <ha-input-text class="max9999-flexrow" data-type="title"
                                       label=${translate('components.ha-dialog.title')}
                                       ${bindTo(this._form, `title`)}
                                       @value-changed=${this._requestRender}
                        ></ha-input-text>
                        <ha-field-errors .formField=${this._form!.getField(`title`)}
                        ></ha-field-errors>
                    </div>
                    <div class="input-wrapper__creator">
                        <ha-combobox class="max9999-flexrow"
                                     data-type="creator"
                                     label=${translate('general.creator')}
                                     ${bindTo(this._form, `creator`)}
                                     .entity=${Employee}
                        ></ha-combobox>
                        <ha-field-errors .formField=${this._form!.getField(`creator`)}
                        ></ha-field-errors>
                    </div>
                    <div class="input-wrapper__url">
                        <ha-input-text class="max9999-flexrow"
                                       data-type="url"
                                       label=${translate('components.ha-dialog.url')}
                                       placeholder="http://domain.tld"
                                       ${bindTo(this._form, `url`)}
                        ></ha-input-text>
                        <ha-field-errors .formField=${this._form!.getField(`url`)}
                        ></ha-field-errors>
                    </div>
                    <div class="input-wrapper__description">
                        <ha-input-text class="max9999-flexrow" data-type="description"
                                       label=${translate('components.ha-dialog.description')}
                                       ${bindTo(this._form, `description`)}
                        ></ha-input-text>
                        <ha-field-errors .formField=${this._form!.getField(`description`)}
                        ></ha-field-errors>
                    </div>
                    <div class="input-wrapper__shortcut">
                        <ha-input-text class="max9999-flexrow" data-type="shortcut"
                                       maxlength="10"
                                       label=${translate('components.ha-dialog.shortcut')}
                                       ${bindTo(this._form, `shortcut`)}
                        ></ha-input-text>
                        <ha-field-errors .formField=${this._form!.getField(`shortcut`)}
                        ></ha-field-errors>
                    </div>
                    <div class="input-wrapper__color">
                        <ha-input-color class="max9999-flexrow" data-type="color"
                                        label=${translate('components.ha-dialog.color')}
                                        .entity=${Project}
                                        ${bindTo(this._form, `color`)}
                                        @value-changed=${this._requestRender}
                        ></ha-input-color>
                        <ha-field-errors .formField=${this._form!.getField(`color`)}
                        ></ha-field-errors>
                    </div>
                    <div class="input-wrapper__text-color">
                        ${renderTextColorFieldOfForm(this._form!, bindTo(this._form, `textColor`), this._requestRender)}
                    </div>
                    <div class="project-edit__milestones max9999-flexrow">
                        <label>${translate(`ha-boards-view.edit-project.milestones.header`)}</label>                       

                        <!-- render milestones: begin -->
                        ${this._renderMilestones()}
                        <!-- render milestones: end -->
                        <button type="button"
                                class="buttonsize-icon buttonsize-normal add-milestone"
                                @click=${() => this._onClickAddMilestone()}
                        >
                            <div class="hover-effect"></div>
                        </button>
                    </div>
                </main>
            </ha-form-project>
        `
    }

    private async _initializeForm(): Promise<void> {
        this._formTeardown$.next();
        this._form = FormBuilder.buildForm(`project`, [
            {
                type: FieldType.Primitive,
                key: `title`,
                initialValue: this.project!.title ?? ``,
                validations: [
                    new MinLengthStringValidator({
                        min: 3,
                        validationMessageFn: () => get(`ha-boards-view.edit-project.title.validations.min-length`)
                    })
                ]
            },
            {
                type: FieldType.Primitive,
                key: `creator`,
                initialValue: this.project!.creator ?? null,
                validations: [
                    new RequiredValidator()
                ]
            },
            {
                type: FieldType.Primitive,
                key: `url`,
                // ?? not ||: `||` also replaces the EMPTY string, which is what put the
                // sample text into the field as a real value and then saved it. The text
                // belongs in the placeholder, where it is a hint and not content.
                initialValue: this.project!.url ?? null,
                validations: [
                    new UrlStringValidator()
                ]
            },
            {
                type: FieldType.Primitive,
                key: `description`,
                initialValue: this.project!.description ?? ``,
                validations: []
            },
            {
                type: FieldType.Primitive,
                key: `shortcut`,
                initialValue: this.project!.shortcut ?? ``,
                validations: []
            },
            {
                type: FieldType.Primitive,
                key: `color`,
                initialValue: this.project!.color ?? `#FFFFFF`,
                validations: [
                    new PatternStringValidator({
                        pattern: new RegExp(`^#([0-9a-fA-F]{3}){1,2}$`),
                        validationMessageFn: () => get(`ha-boards-view.edit-project.color.validations.pattern`)
                    })
                ]
            },
            {
                type: FieldType.Primitive,
                key: `textColor`,
                // null = "Automatisch" (feature plan 0074). No PatternStringValidator: it would test the text "null".
                initialValue: this.project!.textColor ?? null,
                validations: []
            },
            {
                type: FieldType.Complex,
                key: `milestones`,
                // A project without milestones shows one placeholder. It goes into a new array, never
                // into the one the project's milestones promise resolves to: pushed there, the loaded
                // project would list an unsaved issue, which no reload can find.
                children: (await this.project!.milestones.then(async milestones => {
                    this._milestoneUids = new Set(milestones.map(milestone => milestone.uid));

                    return milestones.length > 0 ? milestones : [await this._createMilestone()];
                })).map(milestone => {
                    const isMultiple = milestone.timeRange.count(`days`) > 1;
                    const children: Array<PrimitiveFieldConfiguration<any>> = [
                        {
                            type: FieldType.Primitive,
                            key: `start`,
                            initialValue: milestone.start,
                            validations: [],
                            compare: (dateA: DateTime, dateB: DateTime) => dateA.hasSame(dateB, `day`)
                        },
                    ];

                    if (isMultiple) {
                        children.push({
                            type: FieldType.Primitive,
                            key: `end`,
                            initialValue: milestone.end.minus({day: 1}),
                            validations: [],
                            compare: (dateA: DateTime, dateB: DateTime) => dateA.hasSame(dateB, `day`)
                        });
                    }

                    children.push({
                        type: FieldType.Primitive,
                        key: `isMultipleDays`,
                        initialValue: isMultiple,
                        validations: [],
                    })

                    return <ComplexFieldConfiguration>{
                        type: FieldType.Complex,
                        key: milestone.uid,
                        payload: {milestone},
                        validations: [
                            new MilestoneStartBeforeEndValidator({validationMessageFn: () => get(`ha-boards-view.edit-project.milestones.milestone.validations.start-before-end`)})
                        ],
                        children,
                    }
                }),
                validations: [
                    new MinLengthObjectValidator({
                        min: 1,
                        validationMessageFn: () => get(`ha-boards-view.edit-project.milestones.milestone.validations.minimum-length`)
                    })
                ],
                payload: {milestone: Issue}
            }
        ]);

        const formEnd$ = merge(this._formTeardown$, this._disconnected$);
        this._form.getField(`milestones`).event$.pipe(
            takeUntil(formEnd$),
            filter((notification: FormControlNotification) => notification.event === "value.changed" && notification.formControl!.key === `isMultipleDays`)
        ).subscribe(notification => {
            const milestoneFormControl = notification.formControl!.parent as ComplexFormControl<any, any>;

            const {isMultipleDays, start} = milestoneFormControl.getValue();
            if (isMultipleDays && !milestoneFormControl.hasField(`end`)) {
                milestoneFormControl.addField(
                    new PrimitiveFormControl<DateTime, undefined>(`end`, start.plus({day: 1}), [
                        new RequiredValidator()
                    ])
                );
            } else if (!isMultipleDays && milestoneFormControl.hasField(`end`)) {
                milestoneFormControl.getField(`end`)!.remove();
            }
        });

        // render after field added or removed
        this._form.event$.pipe(
            takeUntil(formEnd$),
            filter((notification: FormControlNotification) => notification.event.startsWith(`childFormControl.`) || notification.event === "value.changed")
        ).subscribe(notification => {
            const [, ...subpath] = notification.formControl!.path.split(`.`);
            const path = subpath.join(`.`);
            this._changedValues.set(path as keyof FormData, notification);
            this.requestUpdate(`_form`, null)
        });
    }

    /**
     * Renders action buttons for save and reset functionalities.
     *
     * The save button is enabled or disabled based on the `_isSavable` property,
     * and triggers the `_onClickSaveButton` handler when clicked.
     *
     * The reset button is enabled or disabled based on the `_form?.isDirty` property,
     * and triggers the `_onClickResetButton` handler when clicked.
     *
     * @return {HTMLTemplateResult} A template result containing the rendered buttons.
     */
    private _renderActionButtons(): HTMLTemplateResult {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveButton}
                
                        data-label=${translate('general.save')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="save-and-close buttonform-marker"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveAndCloseButton}
                
                        data-label=${translate('general.save-and-close')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="reset buttonform-marker"
                        ?disabled=${!this._form?.isDirty}
                        @click=${this._resetForm}
                
                        data-label=${translate('general.reset')}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    /**
     * Saves, then reloads the project once, so the form continues from what the backend stored.
     * The reload runs only after a successful save: after a failed one it would discard the edits.
     *
     * @internal Public so unit tests can press the save button.
     */
    public async _onClickSaveButton(): Promise<void> {
        this._saving = true;
        try {
            if (await this._save()) {
                await this._reloadProject();
            }
        } finally {
            this._saving = false;
        }
    }

    /** Saves the entity and dispatches a CLOSE command to dismiss the dialog; no reload, it closes. */
    private async _onClickSaveAndCloseButton(): Promise<void> {
        this._saving = true;
        try {
            await this._save();
        } finally {
            this._saving = false;
        }
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0
        });
    }

    /** Sends the project's changes as one bulk command; true when the backend accepted it. */
    private async _save(): Promise<boolean> {
        const commands = this.project!.isNew()
            ? this._saveNewProject()
            : this._saveUpdatedProject();

        return await container.resolve(CommandBus).dispatchCommand(new BulkCommand(commands)) === true;
    }

    /**
     * Reloads the project and its saved milestones and continues with the FRESH project instance.
     * reloadEntity() loads a new instance but returns the old one, so the old instance's milestones
     * would be stale. Assigning `project` lets willUpdate rebuild the form: one trigger, one build.
     */
    private async _reloadProject(): Promise<void> {
        const project = this.project!;
        const issueRepository = container.resolve(IssueRepository);
        const projectRepository = container.resolve(ProjectRepository);

        try {
            const saved = (await project.milestones).filter(milestone => !milestone.isNew());
            await Promise.all<Issue | Project>([
                ...saved.map(milestone => issueRepository.reloadEntity(milestone)),
                projectRepository.reloadEntity(project),
            ]);

            const reloaded = await projectRepository.findOneById(project.uid);
            if (!reloaded) {
                console.warn(`Project ${project.uid} could not be reloaded after saving; the dialog keeps the old state.`);
                return;
            }

            this._changedValues.clear();
            this.project = reloaded;
        } catch (error) {
            console.error(`Reloading project ${project.uid} failed:`, error);
        }
    }

    /**
     * Resets the form to its initial state by performing the following actions:
     * - Ends the streams of the current form (not the entity listener).
     * - Clears any changed values that were tracked.
     * - Reinitializes the form structure or data.
     * - Sets the title to the initial title or a default value if no initial title exists.
     * - Requests an update to refresh the UI with the reset state.
     *
     * @return {void} This method does not return any value.
     */
    /** @internal Public so unit tests can press the reset button. */
    public _resetForm(): void {
        this._formTeardown$.next();
        this._changedValues.clear();
        this.title = this._initialTitle ?? `Project Edit`;
        this.requestUpdate();

        this._initializeForm().then();
    }

    /**
     * Handles the click event for adding a new milestone. This method calculates the next milestone's
     * start date based on the latest milestone's end date and creates a new milestone form control.
     * The new form control is then added to the `milestones` form control.
     *
     * @return {Promise<void>} Resolves when the milestone is successfully created and added to the form control.
     */
    private async _onClickAddMilestone(): Promise<void> {
        const milestonesFormControl = this._form!.getField(`milestones`)! as ComplexFormControl<any, any>;
        const lastestDateTime = Array.from(milestonesFormControl.children)
            .map((milestoneFormControl: IFormControl<any, any>) => {
                const {isMultipleDays, start, end} = milestoneFormControl.getValue();

                return (isMultipleDays ? end : start).plus({day: 1});
            })
            .reduce((latest: DateTime | null, current: DateTime) => latest && latest > current ? latest : current, null);

        const isMultipleDays = false;
        const milestone = await this._createMilestone(lastestDateTime ?? DateHelper.today);
        const milestoneFormControl = FormBuilder.buildForm(milestone.uid, [
            {
                type: FieldType.Primitive,
                key: `start`,
                initialValue: milestone.start,
                payload: {disconnected$: new Subject<void>()},
            },
            {
                type: FieldType.Primitive,
                key: `isMultipleDays`,
                initialValue: false,
            }
        ], [
            new MilestoneStartBeforeEndValidator()
        ], {milestone, isMultipleDays});

        milestonesFormControl.addField(milestoneFormControl);
    }

    /**
     * Creates a milestone within the project with a specified start date.
     *
     * @param {DateTime} [start=DateHelper.today] - The start date for the milestone. Defaults to the current date.
     * @return {Promise<Issue>} A promise that resolves to the newly created issue representing the milestone.
     */
    private _createMilestone(start: DateTime = DateHelper.today): Promise<Issue> {
        return container.resolve(IssueRepository).createTimeFrame(
            this.project!,
            Interval.fromDateTimes(start, start.plus({day: 1}))
        )
    }

    /**
     * Handles the event triggered when the remove milestone button is clicked.
     * Removes the specified milestone form control from the form.
     *
     * @param {IFormControl<any, any>} milestoneFormControl - The form control representing the*/
    private _onClickRemoveMilestone(milestoneFormControl: IFormControl<any, any>): void {
        milestoneFormControl.remove();
    }

    private _saveNewProject(): ICommand[] {
        const commands = [];

        // Non-null: ComplexFormControl.getValue() always builds and returns an object.
        const formValues = this._form!.getValue()!;

        for (const [key, value] of Object.entries(formValues)) {
            switch (key) {
                case `milestones`:
                    for (const uid of Object.keys(value as Record<string, unknown>)) {
                        commands.unshift(this._createMilestoneCommand(this._form?.getField<MilestoneFormValue, MilestonePayload>(`milestones.${uid}`)!));
                    }
                    break;
                default:
                    if (!Reflect.has(this.project!, key)) {
                        throw new Error(`Unknown property ${key} in project`);
                    }
                    // Creating a project walks EVERY form value, not just the changed ones,
                    // and the transformer then sends the full record — so an untouched URL
                    // field would be written verbatim.
                    Reflect.set(this.project!, key, key === `url`
                        ? coerceUrlToNullIfBlank(value as string | null)
                        : value);
                    break;
            }
        }

        commands.unshift(new CreateProjectCommand(this.project!));

        return commands;
    }

    private _saveUpdatedProject(): ICommand[] {

        for (const [path, notification] of this._changedValues.entries()) {
            switch (path) {
                case `title`:
                    this.project!.title = notification.formControl!.getValue() as string;
                    break;
                case `creator`:
                    this.project!.creator = notification.formControl!.getValue() as Employee;
                    break;
                case `url`:
                    this.project!.url = coerceUrlToNullIfBlank(
                        notification.formControl!.getValue() as string | null,
                    );
                    break;
                case `description`:
                    this.project!.description = notification.formControl!.getValue() as string;
                    break;
                case `color`:
                    this.project!.color = notification.formControl!.getValue() as string;
                    break;
                case `textColor`:
                    this.project!.textColor = notification.formControl!.getValue() as string | null;
                    break;
                case `shortcut`:
                    this.project!.shortcut = notification.formControl!.getValue() as string;
                    break;
                default:
                // do nothing
            }
        }

        const commands: ICommand[] = [];

        if (this.project?.isDirty()) {
            commands.push(new UpdateProjectCommand(this.project!));
        }

        const milestonesFormControl = this._form?.getField(`milestones`) as ComplexFormControl<unknown, unknown>;

        if (milestonesFormControl.isDirty) {

            // deleted
            commands.push(...deletedMilestones(this._changedValues.entries())
                .map(milestone => new DeleteIssueCommand(milestone)));

            // changed or created
            for (const milestoneFormControl of milestonesFormControl.children as IterableIterator<IFormControl<MilestoneFormValue, MilestonePayload>>) {
                // Non-null: every child of `milestones` is built with a `{milestone}` payload.
                const command = milestoneFormControl.payload!.milestone.isNew()
                    ? this._createMilestoneCommand(milestoneFormControl)
                    : (milestoneFormControl.isDirty ? this._updateMilestoneCommand(milestoneFormControl) : null);

                if (command) {
                    commands.push(command);
                }
            }
        }

        return commands;
    }

    private _createMilestoneCommand(milestoneFormControl: IFormControl<MilestoneFormValue, MilestonePayload>): ICommand {
        return new CreateIssueCommand(this._getUpdatedMilestoneByFormControl(milestoneFormControl));
    }

    private _updateMilestoneCommand(milestoneFormControl: IFormControl<MilestoneFormValue, MilestonePayload>): ICommand {
        return new UpdateIssueCommand(this._getUpdatedMilestoneByFormControl(milestoneFormControl));
    }

    private _getUpdatedMilestoneByFormControl(milestoneFormControl: IFormControl<MilestoneFormValue, MilestonePayload>): Issue {
        const {milestone} = milestoneFormControl.payload!;
        // Non-null: the control is a milestone ComplexFormControl, whose getValue() always returns an object.
        const {start, end, isMultipleDays} = milestoneFormControl.getValue()!;
        milestone.timeRange = Interval.fromDateTimes(
            start,
            isMultipleDays
                // Non-null: `end` exists while `isMultipleDays` is true (see MilestoneFormValue).
                ? end!.plus({day: 1})
                : start.plus({day: 1})
        );

        return milestone;
    }

    private _renderMilestones(): HTMLTemplateResult {
        const formControls = (this._form!.getField(`milestones`) as ComplexFormControl<any, any>).children;

        return html`
            <div class="milestones">
                ${repeat(
            formControls,
            (child: IFormControl<any, any>) => child.key,
            (milestoneFormControl: IFormControl<MilestoneFormValue, MilestonePayload>) => this._renderMilestone(milestoneFormControl)
        )}
            </div>
            <ha-field-errors .formField=${this._form!.getField(`milestones`)}></ha-field-errors>
        `
    }

    private _renderMilestone(milestoneFormControl: IFormControl<MilestoneFormValue, MilestonePayload>): HTMLTemplateResult {
        // Non-null: the control is a milestone ComplexFormControl, whose getValue() always returns an object.
        const {isMultipleDays} = milestoneFormControl.getValue()!;

        return html`
            <div class="milestone">
                <ha-input-date class="max9999-flexcolumn" data-type="start"
                               label=${translate(`ha-boards-view.edit-project.milestones.milestone.${isMultipleDays ? `start-date` : `due-date`}.label`)}
                               ${bindTo(this._form!, `${milestoneFormControl.path}.start`)}
                ></ha-input-date>
                ${when(isMultipleDays, () => html`
                    <ha-input-date class="max9999-flexcolumn" data-type="end"
                                   label=${translate('ha-boards-view.edit-project.milestones.milestone.end-date.label')}
                                   ${bindTo(this._form!, `${milestoneFormControl.path}.end`)}
                    ></ha-input-date>
                `)}
                <ha-input-checkbox class=" " data-type="isMultipleDays" is-toggle
                                   label=${translate('ha-boards-view.edit-project.milestones.milestone.multiple-days.label')}
                                   ${bindTo(this._form!, `${milestoneFormControl.path}.isMultipleDays`)}
                ></ha-input-checkbox>

                <button type="button"
                        class="buttonsize-icon buttonsize-normal  milestone-remove"
                        @click=${() => this._onClickRemoveMilestone(milestoneFormControl)}
                >
                    <div class="hover-effect"></div>
                </button>
                <ha-field-errors .formField=${milestoneFormControl}></ha-field-errors>
            </div>
        `;
    }

    /**
     * Reloads the project when it or one of its milestones was changed ELSEWHERE (another tab or user).
     * The dialog's own save reloads by itself, so its messages are skipped while it runs. A burst of
     * messages leads to one reload.
     */
    private _registerListeners() {
        container.resolve(MessageBroker).messages$.pipe(
            takeUntil(this._disconnected$),
            filter((message: BaseMessage<any>) => !this._saving && this._concernsThisProject(message)),
            debounceTime(0),
            exhaustMap(() => this._reloadProject()),
        ).subscribe();
    }

    /** Whether a broker message is about this dialog's project or one of its milestones. */
    private _concernsThisProject(message: BaseMessage<any>): boolean {
        const uids = new Set([this.project?.uid, ...this._milestoneUids]);

        switch (message.type) {
            case `entity.created`:
                return uids.has(message.payload.uid) || message.payload.entity?.project?.uid === this.project?.uid;
            case `entity.changed`:
                return uids.has(message.payload.uid);
            case `entity.deleted`:
                return uids.has(message.payload.entity?.uid);
            default:
                return false;
        }
    }
}
